import toast from '@/components/toast';
import { env, Native } from '@hb/hbridge';
import html2canvas from 'html2canvas';
/**
 * 图片转base64，如果传入的是url，则将url图片转base64，如果传入的是querySelector，则将querySelector的元素通过html2canvas转成base64
 * @param {Object} params 参数对象
 * @param {string} params.url 图片url
 * @param {string} params.querySelector 选择器 如果URL存在，则会忽略querySelector
 * @returns {Promise<{success: boolean, data: string}>} 返回promise对象
 * 成功时返回{success: true, data: base64}
 * 失败时返回{success: false}
 */
export async function imageToDataURL({ url, querySelector, radio = 1 }) {
  if (url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // 允许跨域
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/jpeg');
        resolve({
          success: true,
          data: dataURL,
        });
      };
      img.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('图片加载失败', err);
        toast.show('图片加载失败');
        resolve({
          success: false,
          errorMessage: '图片加载失败',
        });
      };
      img.src = url;
    });
  }
  if (querySelector) {
    const container = document.querySelector(querySelector);
    try {
      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: true,
        width: container.offsetWidth / radio,
        height: container.offsetHeight / radio,
        scale: radio,
        backgroundColor: '#fff',
      });
      const image = canvas.toDataURL('image/jpeg');
      const dataURL = image;
      return {
        success: true,
        data: dataURL,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('图片加载失败', err);
      toast.show('图片加载失败');
      return {
        success: false,
        errorMessage: '图片加载失败',
      };
    }
  }
  return {
    success: false,
    errorMessage: '图片资源错误',
  };
}

/**
 * 将url图片保存到相册 如果传入的是url，则将url图片保存到相册，如果传入的是querySelector，则将querySelector的元素转化成图片后保存到相册
 * @param {string} url 图片url
 * @returns {Promise<{success: boolean, data: string}>} 返回promise对象
 * 成功时返回{success: true, data: base64}
 * 失败时返回{success: false}
 */
export async function saveImageToAlbum({ url, querySelector = '', radio = 1 }) {
  const { success, data } = await imageToDataURL({ url, querySelector, radio });
  if (success) {
    const saveOrSharePic = data.replace('data:image/jpeg;base64,', '');
    if (env.appVersion >= '6.54.0') {
      Native.callNative({
        classMap: 'business/moments',
        method: env.isIOS ? 'savePicture' : 'saveMomentPicture',
        params: { base64Data: saveOrSharePic },
      });
    } else {
      Native.callNative({
        classMap: 'SystemUtil',
        method: 'savePicture',
        params: { base64Data: saveOrSharePic },
      });
    }
  }
}


export function checkWebpSupport() { // 检测 WebP 支持性
  try {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (e) {
    return false;
  }
}


export function getImageUrl(url, { width, height, quality } = {}) {
  if (!url) return '';

  // 如果是 base64，直接返回
  if (url.indexOf('data:image') === 0) {
    return url;
  }

  const commands = [];

  // 1. 尺寸裁剪 (m_lfit: 等比缩放，限制在设定在矩形框内)
  if (width || height) {
    let resizeCmd = 'resize';
    if (width) resizeCmd += `,w_${width}`;
    if (height) resizeCmd += `,h_${height}`;
    resizeCmd += ',m_lfit';
    commands.push(resizeCmd);
  }

  // 2. 质量压缩
  if (quality) {
    commands.push(`quality,Q_${quality}`);
  }

  // 3. WebP 格式转换
  const originUrl = url.split('?')[0]; // 获取无参数的原始 URL
  if (checkWebpSupport() && !originUrl.toLowerCase().endsWith('.webp') && !url.includes('format,webp')) {
    commands.push('format,webp');
  }

  if (commands.length === 0) {
    return url;
  }

  const newCommandStr = commands.join('/');

  // 如果已有 x-oss-process 参数，则追加新命令
  if (url.includes('x-oss-process=')) {
    return url.replace(/(x-oss-process=[^&]+)/, (match) => `${match}/${newCommandStr}`);
  }

  const processStr = `image/${newCommandStr}`;
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}x-oss-process=${processStr}`;
}
